export type OverlayRect = {
  width: number;
  height: number;
};

export type ImageRect = OverlayRect & {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type EquineOverlayGeometry = {
  viewport: OverlayRect;
  image: ImageRect;
};

type OverlayPositionInput = EquineOverlayGeometry & {
  overlay: OverlayRect;
};

export type OverlayPosition = {
  left: number;
  top: number;
  placement: 'left-dead-space' | 'top-dead-space' | 'image-inset';
};

const MARGIN = 8;
const RESERVED_TOP = 28;
const RESERVED_BOTTOM = 3;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

/**
 * Prefer unused letterbox/pillarbox space, then use a proportional image inset.
 * Every result is clamped to the available viewport so a tall identity stack
 * cannot be clipped in portrait or short 2-up layouts.
 */
export function computeEquineOverlayPosition({
  viewport,
  image,
  overlay,
}: OverlayPositionInput): OverlayPosition {
  const maxLeft = viewport.width - overlay.width - MARGIN;
  const maxTop = viewport.height - overlay.height - RESERVED_BOTTOM;

  if (image.left >= overlay.width + MARGIN * 2) {
    return {
      left: clamp(image.left - overlay.width - MARGIN, MARGIN, maxLeft),
      top: clamp(image.top, RESERVED_TOP, maxTop),
      placement: 'left-dead-space',
    };
  }

  if (image.top >= overlay.height + RESERVED_TOP + MARGIN) {
    return {
      left: clamp(image.left, MARGIN, maxLeft),
      top: clamp(image.top - overlay.height - MARGIN, RESERVED_TOP, maxTop),
      placement: 'top-dead-space',
    };
  }

  const imageHeight = Math.max(0, image.bottom - image.top || image.height);
  return {
    left: clamp(image.left + MARGIN, MARGIN, maxLeft),
    top: clamp(image.top + imageHeight * 0.18, RESERVED_TOP, maxTop),
    placement: 'image-inset',
  };
}
